import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import AppLayout from "@/layouts/AppLayout";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Athletes from "@/pages/Athletes";
import AthleteProfile from "@/pages/AthleteProfile";
import Groups from "@/pages/Groups";
import Training from "@/pages/Training";
import TrainingHub from "@/pages/TrainingHub";
import TrainingDetail from "@/pages/TrainingDetail";
import Tests from "@/pages/Tests";
import Fencing from "@/pages/Fencing";
import CompetitiveSession from "@/pages/CompetitiveSession";
import Competitions from "@/pages/Competitions";
import CompetitionDetail from "@/pages/CompetitionDetail";
import Reports from "@/pages/Reports";
import Exercises from "@/pages/Exercises";
import Injuries from "@/pages/Injuries";
import Statistics from "@/pages/Statistics";
import AthleteStats from "@/pages/AthleteStats";
import AIChat from "@/pages/AIChat";
import Settings from "@/pages/Settings";
import SeasonsSettings from "@/pages/SeasonsSettings";
import Attendance from "@/pages/Attendance";
import Programming from "@/pages/Programming";
import StaffControl from "@/pages/StaffControl";
import AthleteDuplicates from "@/pages/AthleteDuplicates";
import ErrorBoundary from "@/components/ErrorBoundary";
import { SeasonProvider } from "@/context/SeasonContext";
import { VersionProvider } from "@/context/VersionContext";

function Protected({ children }) {
  const { user, ready } = useAuth();
  const loc = useLocation();
  if (!ready) return <div className="min-h-screen flex items-center justify-center text-zinc-500">Caricamento…</div>;
  if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/registrati" element={<Register />} />
      <Route element={<Protected><AppLayout /></Protected>}>
        <Route path="/" element={<ErrorBoundary label="la dashboard"><Dashboard /></ErrorBoundary>} />
        <Route path="/atleti" element={<ErrorBoundary label="l'elenco atleti"><Athletes /></ErrorBoundary>} />
        <Route path="/atleti/:id" element={<ErrorBoundary label="la scheda atleta"><AthleteProfile /></ErrorBoundary>} />
        <Route path="/gruppi" element={<Groups />} />
        <Route path="/allenamento" element={<Training />} />
        <Route path="/allenamento/oggi" element={<ErrorBoundary label="l'area tecnica"><TrainingHub /></ErrorBoundary>} />
        <Route path="/allenamento/:id" element={<TrainingDetail />} />
        <Route path="/oggi" element={<Navigate to="/" replace />} />
        <Route path="/programmazione" element={<ErrorBoundary label="la programmazione"><Programming /></ErrorBoundary>} />
        <Route path="/test" element={<Tests />} />
        <Route path="/scherma" element={<Fencing />} />
        <Route path="/scherma/simulazione/:id" element={<CompetitiveSession />} />
        <Route path="/gare" element={<Competitions />} />
        <Route path="/gare/:id" element={<CompetitionDetail />} />
        <Route path="/infortuni" element={<Injuries />} />
        <Route path="/statistiche" element={<Statistics />} />
        <Route path="/staff-control" element={<ErrorBoundary label="lo staff control"><StaffControl /></ErrorBoundary>} />
        <Route path="/statistiche/:id" element={<AthleteStats />} />
        <Route path="/ai-coach" element={<AIChat />} />
        <Route path="/report" element={<Reports />} />
        <Route path="/esercizi" element={<Exercises />} />
        <Route path="/impostazioni" element={<Settings />} />
        <Route path="/impostazioni/stagioni" element={<SeasonsSettings />} />
        <Route path="/presenze" element={<ErrorBoundary label="le presenze"><Attendance /></ErrorBoundary>} />
        <Route path="/atleti/duplicati" element={<AthleteDuplicates />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    // Register service worker (PWA) for offline read-only capability
    if ("serviceWorker" in navigator) {
      const hadController = !!navigator.serviceWorker.controller;
      navigator.serviceWorker.register("/sw.js", { updateViaCache: "none" }).then((reg) => reg.update().catch(() => {})).catch(() => {});
      // Nuovo SW ha preso il controllo di una pagina servita dal vecchio: ricarica una volta per scaricare il bundle aggiornato
      if (hadController) {
        navigator.serviceWorker.addEventListener("controllerchange", () => {
          if (sessionStorage.getItem("csplab-sw-reloaded")) return;
          sessionStorage.setItem("csplab-sw-reloaded", "1");
          window.location.reload();
        }, { once: true });
      }
    }
  }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <SeasonProvider>
            <VersionProvider>
              <AppRoutes />
              <Toaster position="top-right" />
            </VersionProvider>
          </SeasonProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
